import React, { useMemo, useCallback } from 'react';
import { Box, Text, VStack, HStack } from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';
import { formatMediaLabel } from 'src/features/Settings/utils/formatters';

type MediaSelectListProps = {
  mediaPlatforms: string[];
  selectedMedia: string[];
  onChange: (selectedMedia: string[]) => void;
  isDisabled?: boolean;
};

export const MediaSelectList: React.FC<MediaSelectListProps> = ({
  mediaPlatforms,
  selectedMedia,
  onChange,
  isDisabled = false,
}) => {
  const isAllSelected = useMemo(() => {
    if (mediaPlatforms.length === 0) return false;
    return mediaPlatforms.every((media) => selectedMedia.includes(media));
  }, [mediaPlatforms, selectedMedia]);

  const handleSelectAll = useCallback(() => {
    if (isDisabled || mediaPlatforms.length === 0) return;
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange([...mediaPlatforms]);
    }
  }, [isDisabled, isAllSelected, mediaPlatforms, onChange]);

  const handleToggleMedia = useCallback(
    (media: string) => {
      if (isDisabled) return;
      if (selectedMedia.includes(media)) {
        onChange(selectedMedia.filter((m) => m !== media));
      } else {
        onChange([...selectedMedia, media]);
      }
    },
    [isDisabled, selectedMedia, onChange],
  );

  return (
    <Box
      border="1px solid"
      borderColor="border.layout"
      borderRadius="24px"
      p={6}
      bg="surface.card">
      <Text fontSize="16px" fontWeight={600} color="text.title" mb={6}>
        SELECT MEDIA
      </Text>

      <Box
        border="1px solid"
        borderColor="border.layout"
        borderRadius="md"
        overflow="hidden">
        {/* Header row */}
        <HStack
          px={4}
          py={4}
          justify="space-between"
          bg="surface.hover"
          borderBottom="1px solid"
          borderColor="border.layout">
          <Text fontSize="14px" fontWeight={600} color="darkGray">
            Media Name
          </Text>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor={
              isDisabled || mediaPlatforms.length === 0
                ? 'not-allowed'
                : 'pointer'
            }
            onClick={handleSelectAll}
            w="18px"
            h="18px"
            bg="transparent"
            border="2px solid"
            borderColor={isAllSelected ? 'icon.green' : 'gray.300'}
            color={isAllSelected ? 'icon.green' : 'gray.300'}
            borderRadius="full"
            _hover={{ opacity: isDisabled ? 1 : 0.8 }}>
            {isAllSelected && <Icon iconName="check" width={2} height={2} />}
          </Box>
        </HStack>

        {/* Media list */}
        <VStack spacing={0} align="stretch">
          {mediaPlatforms.length === 0 ? (
            <Box px={4} py={4}>
              <Text fontSize="16px" color="gray.500">
                No media platforms available
              </Text>
            </Box>
          ) : (
            mediaPlatforms.map((media) => {
              const isSelected = selectedMedia.includes(media);
              return (
                <HStack
                  key={media}
                  px={4}
                  py={4}
                  justify="space-between"
                  borderBottom="1px solid"
                  borderColor="border.layout"
                  _last={{ borderBottom: 'none' }}
                  cursor={isDisabled ? 'not-allowed' : 'pointer'}
                  onClick={() => handleToggleMedia(media)}
                  _hover={{ bg: isDisabled ? 'transparent' : 'surface.hover' }}>
                  <Text fontSize="16px" fontWeight={500} color="text.title">
                    {formatMediaLabel(media)}
                  </Text>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="18px"
                    h="18px"
                    bg="transparent"
                    border="2px solid"
                    borderColor={isSelected ? 'icon.green' : 'gray.300'}
                    color={isSelected ? 'icon.green' : 'gray.300'}
                    borderRadius="full"
                    _hover={{ opacity: isDisabled ? 1 : 0.8 }}>
                    {isSelected && (
                      <Icon iconName="check" width={2} height={2} />
                    )}
                  </Box>
                </HStack>
              );
            })
          )}
        </VStack>
      </Box>
    </Box>
  );
};
